/**
 * Enterprise Repository Container (Inversion of Control & Dependency Injection)
 *
 * Provides central registration, resolution, and plug-and-play swapping of repository
 * implementations across all features. Allows seamless switching between production API
 * implementations and in-memory mock repositories for testing, offline execution, and storybooks.
 *
 * Optimized with Lazy Factory Resolution to prevent monolithic module coupling.
 */
import { REPOSITORY_TOKENS, RepositoryToken } from './RepositoryTokens';
import { IVisitorRepository, ApiVisitorRepository } from '../../features/visitor/repository';
import { IUnitRepository, ApiUnitRepository } from '../../features/unit/repository';
import { IMemberRepository, ApiMemberRepository } from '../../features/member/repository';
import { ISocietyRepository, ApiSocietyRepository } from '../../features/society/repository';
import { IFacilityRepository, ApiFacilityRepository } from '../../features/facility/repository';
import { IDashboardRepository, ApiDashboardRepository } from '../../features/dashboard/repository';

import type { IFinanceRepository } from '../../features/finance/repository';
import type { ILedgerRepository } from '../../features/ledger/repository';
import type { IDuesRepository } from '../../features/dues/repository';
import type { IHelpdeskRepository } from '../../features/helpdesk/repository';
import type { ICommitteeRepository } from '../../features/committee/repository';
import type { IComplianceRepository } from '../../features/compliance/repository';
import type { INoticeRepository } from '../../features/communication/repository';
import type { ICommunityRepository } from '../../features/community/repository';
import type { IProfileRepository } from '../../features/profile/repository';
import type { IVendorRepository } from '../../features/vendor/repository';
import type { IUploadRepository } from '../../features/upload/repository';

export class RepositoryContainer {
  private static instance: RepositoryContainer | null = null;
  private registry: Map<string, any> = new Map();

  public static getInstance(): RepositoryContainer {
    if (!RepositoryContainer.instance) {
      RepositoryContainer.instance = new RepositoryContainer();
    }
    return RepositoryContainer.instance;
  }

  /**
   * Register a custom or mock repository implementation for a token
   */
  public register<T>(token: RepositoryToken | string, implementation: T): void {
    this.registry.set(token, implementation);
  }

  /**
   * Resolve a repository by token
   */
  public get<T>(token: RepositoryToken | string): T {
    const service = this.registry.get(token);
    if (!service) {
      throw new Error(`[DI Container] Service for token '${token}' not registered.`);
    }
    return service as T;
  }

  // ── Strongly-typed accessor shortcuts with on-demand lazy initialization ──

  public getVisitorRepository(): IVisitorRepository {
    if (!this.registry.has(REPOSITORY_TOKENS.VISITOR)) {
      this.registry.set(REPOSITORY_TOKENS.VISITOR, new ApiVisitorRepository());
    }
    return this.registry.get(REPOSITORY_TOKENS.VISITOR);
  }

  public getUnitRepository(): IUnitRepository {
    if (!this.registry.has(REPOSITORY_TOKENS.UNIT)) {
      this.registry.set(REPOSITORY_TOKENS.UNIT, new ApiUnitRepository());
    }
    return this.registry.get(REPOSITORY_TOKENS.UNIT);
  }

  public getMemberRepository(): IMemberRepository {
    if (!this.registry.has(REPOSITORY_TOKENS.MEMBER)) {
      this.registry.set(REPOSITORY_TOKENS.MEMBER, new ApiMemberRepository());
    }
    return this.registry.get(REPOSITORY_TOKENS.MEMBER);
  }

  public getSocietyRepository(): ISocietyRepository {
    if (!this.registry.has(REPOSITORY_TOKENS.SOCIETY)) {
      this.registry.set(REPOSITORY_TOKENS.SOCIETY, new ApiSocietyRepository());
    }
    return this.registry.get(REPOSITORY_TOKENS.SOCIETY);
  }

  public getFacilityRepository(): IFacilityRepository {
    if (!this.registry.has(REPOSITORY_TOKENS.FACILITY)) {
      this.registry.set(REPOSITORY_TOKENS.FACILITY, new ApiFacilityRepository());
    }
    return this.registry.get(REPOSITORY_TOKENS.FACILITY);
  }

  public getDashboardRepository(): IDashboardRepository {
    if (!this.registry.has(REPOSITORY_TOKENS.DASHBOARD)) {
      this.registry.set(REPOSITORY_TOKENS.DASHBOARD, new ApiDashboardRepository());
    }
    return this.registry.get(REPOSITORY_TOKENS.DASHBOARD);
  }

  public getFinanceRepository(): IFinanceRepository {
    return this.get<IFinanceRepository>(REPOSITORY_TOKENS.FINANCE);
  }

  public getLedgerRepository(): ILedgerRepository {
    return this.get<ILedgerRepository>(REPOSITORY_TOKENS.LEDGER);
  }

  public getDuesRepository(): IDuesRepository {
    return this.get<IDuesRepository>(REPOSITORY_TOKENS.DUES);
  }

  public getHelpdeskRepository(): IHelpdeskRepository {
    return this.get<IHelpdeskRepository>(REPOSITORY_TOKENS.HELPDESK);
  }

  public getCommitteeRepository(): ICommitteeRepository {
    return this.get<ICommitteeRepository>(REPOSITORY_TOKENS.COMMITTEE);
  }

  public getComplianceRepository(): IComplianceRepository {
    return this.get<IComplianceRepository>(REPOSITORY_TOKENS.COMPLIANCE);
  }

  public getNoticeRepository(): INoticeRepository {
    return this.get<INoticeRepository>(REPOSITORY_TOKENS.NOTICE);
  }

  public getCommunityRepository(): ICommunityRepository {
    return this.get<ICommunityRepository>(REPOSITORY_TOKENS.COMMUNITY);
  }

  public getProfileRepository(): IProfileRepository {
    return this.get<IProfileRepository>(REPOSITORY_TOKENS.PROFILE);
  }

  public getVendorRepository(): IVendorRepository {
    return this.get<IVendorRepository>(REPOSITORY_TOKENS.VENDOR);
  }

  public getUploadRepository(): IUploadRepository {
    return this.get<IUploadRepository>(REPOSITORY_TOKENS.UPLOAD);
  }

  /**
   * Reset container to clean state (useful between tests)
   */
  public reset(): void {
    this.registry.clear();
  }
}

export const repositoryContainer = RepositoryContainer.getInstance();
export default repositoryContainer;
